import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { shortDate } from "../lib/format";
import { toast } from "sonner";
import { ArrowLeft, Undo2, FileText, Wallet, AlertTriangle } from "lucide-react";

export default function ImportHistory() {
  const [busy, setBusy] = useState(false);
  const [imports, setImports] = useState([]);
  const [confirmId, setConfirmId] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/imports");
      setImports(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load history");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const undo = async (iid) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/imports/${iid}/undo`);
      toast.success(
        `Undone · ${data.deleted} deleted · ${data.uncleared} unmarked`
      );
      setConfirmId(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Undo failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="import-history-page">
      <div className="flex items-center justify-between">
        <Link to="/import" className="nb-btn nb-btn-secondary !py-2 !px-3">
          <ArrowLeft size={16} strokeWidth={3} /> Back
        </Link>
        <h1 className="font-display text-2xl font-black">Import History</h1>
        <div className="w-[70px]" />
      </div>

      {imports.length === 0 && !busy && (
        <div
          className="nb-card-lg p-6 text-center"
          data-testid="import-history-empty"
        >
          <FileText size={40} strokeWidth={2.5} className="mx-auto mb-3" />
          <div className="font-display font-black text-xl mb-1">
            No imports yet
          </div>
          <div className="text-sm text-[#4a4a4a] font-medium mb-4">
            Once you upload a bank CSV, it&apos;ll show up here with an undo button.
          </div>
          <Link
            to="/import"
            data-testid="go-import-btn"
            className="nb-btn nb-btn-dark !py-3 inline-flex"
          >
            Upload one now
          </Link>
        </div>
      )}

      {imports.map((imp) => (
        <div
          key={imp.id}
          data-testid={`import-batch-${imp.id}`}
          className="nb-card p-4 space-y-2"
          style={{ opacity: imp.undone ? 0.55 : 1 }}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="font-display font-black text-lg truncate flex items-center gap-2">
                <FileText size={16} strokeWidth={3} className="shrink-0" />
                {imp.filename || "(no filename)"}
                {imp.undone && (
                  <span
                    className="nb-tag !text-[9px] !py-0 !px-2 bg-gray-300"
                    data-testid={`undone-tag-${imp.id}`}
                  >
                    UNDONE
                  </span>
                )}
              </div>
              <div className="text-xs text-[#4a4a4a] font-medium mt-0.5 flex items-center gap-1.5 flex-wrap">
                <span>{shortDate(imp.created_at)}</span>
                <span>·</span>
                <Wallet size={11} strokeWidth={3} />
                <span>{imp.account_name}</span>
              </div>
              <div className="text-sm font-bold mt-1.5">
                <span data-testid={`batch-created-${imp.id}`}>
                  {imp.created_count}
                </span>{" "}
                added ·{" "}
                <span data-testid={`batch-matched-${imp.id}`}>
                  {imp.matched_count}
                </span>{" "}
                cleared
              </div>
            </div>
            {!imp.undone && (
              <button
                data-testid={`undo-btn-${imp.id}`}
                onClick={() => setConfirmId(imp.id)}
                className="nb-btn nb-btn-secondary !py-2 !px-3 shrink-0"
                disabled={busy}
                title="Undo this import"
              >
                <Undo2 size={16} strokeWidth={3} />
                <span className="hidden sm:inline">Undo</span>
              </button>
            )}
          </div>

          {confirmId === imp.id && (
            <div
              className="mt-3 p-3 border-2 border-[#1a1a1a] rounded-xl bg-[#fdecc8]"
              data-testid={`confirm-undo-${imp.id}`}
            >
              <div className="flex items-start gap-2 mb-2">
                <AlertTriangle size={18} strokeWidth={3} className="shrink-0 mt-0.5" />
                <div className="text-sm font-bold">
                  Undo this import? {imp.created_count} added transaction
                  {imp.created_count === 1 ? "" : "s"} will be deleted and{" "}
                  {imp.matched_count} cleared txn
                  {imp.matched_count === 1 ? "" : "s"} will be un-cleared.
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  data-testid={`confirm-undo-yes-${imp.id}`}
                  onClick={() => undo(imp.id)}
                  className="nb-btn nb-btn-dark !py-2 flex-1"
                  disabled={busy}
                >
                  {busy ? "Undoing…" : "Yes, undo"}
                </button>
                <button
                  data-testid={`confirm-undo-cancel-${imp.id}`}
                  onClick={() => setConfirmId(null)}
                  className="nb-btn nb-btn-secondary !py-2 flex-1"
                  disabled={busy}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
